import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { api } from '~/data/utils/axios-instance';
import { PermissionType } from '~/data/sdk';
import { AUTH_BYPASS_ENABLED } from '~/lib/dev-auth-bypass.constants';

export interface UserPermission {
  id: string;
  activityId: string;
  type: PermissionType;
}

export interface PermissionsState {
  // Auth0 permissions (legacy)
  auth0Permissions: string[];
  // New permissions from API
  userPermissions: UserPermission[];
  // Combined permissions for easy access
  allPermissions: string[];
  // Whether the current user's role has CFO / copilot flags, from GET /roles/current
  isCFO: boolean;
  hasCopilotAccess: boolean;
  // True once a GET /roles/current call has settled (success or failure) at least once
  // this session. Distinct from `lastFetched`/cache validity — this tracks whether we've
  // ever determined the role at all, so consumers can tell "still unknown" apart from
  // "known and cached".
  roleFetched: boolean;
  // Loading state
  isLoading: boolean;
  // Last fetch timestamp
  lastFetched: number | null;
  // Cache duration in milliseconds (5 minutes)
  cacheDuration: number;

  // Actions
  setAuth0Permissions: (permissions: string[]) => void;
  setUserPermissions: (permissions: UserPermission[]) => void;
  fetchUserPermissions: (roleId: string) => Promise<void>;
  // Records the CFO / copilot-access flags from a successful GET /roles/current call.
  setCurrentRole: (flags: { isCFO: boolean; hasCopilotAccess: boolean }) => void;
  // Marks a role-fetch attempt as settled without changing the last-known isCFO/hasCopilotAccess
  // values — used on fetch failure so we stop blocking on `roleFetched` while preserving
  // whatever role info we already had.
  markRoleFetchAttempted: () => void;
  clearPermissions: () => void;
  hasPermission: (permission: string) => boolean;
  hasActivityPermission: (activityId: string, permissionType: PermissionType) => boolean;
  isAdmin: () => boolean;
  isOwner: (activityId: string) => boolean;
  isCacheValid: () => boolean;
}

/**
 * Permissions Store
 *
 * This store manages user permissions from both Auth0 (legacy) and the new permissions API.
 * It provides a unified interface for permission checking while supporting the migration.
 *
 * Migration Strategy:
 * 1. Initially: Use both Auth0 permissions and API permissions
 * 2. During migration: Gradually replace Auth0 checks with API-based checks
 * 3. Final state: Use only API permissions
 *
 * Features:
 * - Automatic caching (5 minutes)
 * - Session-based clearing
 * - Fallback to Auth0 permissions when API is unavailable
 * - Activity-specific permission checking
 */
export const usePermissionsStore = create<PermissionsState>()(
  devtools(
    (set, get) => ({
      // Initial state
      auth0Permissions: [],
      userPermissions: [],
      allPermissions: [],
      isCFO: false,
      hasCopilotAccess: false,
      roleFetched: false,
      isLoading: false,
      lastFetched: null,
      // Keep the TTL short so admin-driven RBAC changes propagate to the
      // affected user within ~30s even without an explicit invalidation.
      // `invalidateRbacCaches` + the RBAC SSE stream are the primary paths;
      // this is just the passive ceiling.
      cacheDuration: 30 * 1000,

      // Set Auth0 permissions (legacy - fallback)
      setAuth0Permissions: (permissions: string[]) => {
        set((state) => {
          // API permissions take priority, Auth0 is fallback
          const apiPermissions = state.userPermissions.map((p) => p.type);
          const allPermissions = [...apiPermissions, ...permissions];
          return {
            auth0Permissions: permissions,
            allPermissions: [...new Set(allPermissions)], // Remove duplicates
          };
        });
      },

      // Set user permissions from API (priority)
      setUserPermissions: (permissions: UserPermission[]) => {
        set((state) => {
          // API permissions take priority over Auth0
          const apiPermissions = permissions.map((p) => p.type);
          const fallbackPermissions = state.auth0Permissions;
          const allPermissions = [...apiPermissions, ...fallbackPermissions];

          return {
            userPermissions: permissions,
            allPermissions: [...new Set(allPermissions)], // Remove duplicates
            lastFetched: Date.now(), // Update cache timestamp
          };
        });
      },

      // Fetch user permissions from API by role ID
      fetchUserPermissions: async (roleId: string) => {
        const state = get();

        // Check if cache is still valid
        if (state.lastFetched && Date.now() - state.lastFetched < state.cacheDuration) {
          return; // Use cached data
        }

        set({ isLoading: true });

        try {
          // Use the actual API endpoint: GET /api/roles/:id/permissions
          const response = await api.role.getRolePermissions({ id: roleId });
          const rolePermissions = response.data;

          // Transform the API response to our UserPermission format
          const permissions: UserPermission[] =
            rolePermissions.permissions
              ?.map((permission) => ({
                id: permission.id,
                activityId: permission.activityId || '',
                type: permission.type,
              }))
              .filter((permission) => permission.activityId) || [];

          set((state) => ({
            userPermissions: permissions,
            allPermissions: [...state.auth0Permissions, ...permissions.map((p) => p.type)],
            lastFetched: Date.now(),
            isLoading: false,
          }));
        } catch (error) {
          console.error('Failed to fetch user permissions for roleId:', roleId, error);
          // On error, we fall back to Auth0 permissions only
          set({ isLoading: false });
        }
      },

      // Record the CFO / copilot-access flags from a successful GET /roles/current call
      setCurrentRole: (flags: { isCFO: boolean; hasCopilotAccess: boolean }) => {
        set({
          isCFO: flags.isCFO,
          hasCopilotAccess: flags.hasCopilotAccess,
          roleFetched: true,
        });
      },

      // Mark a role-fetch attempt as settled (e.g. after a failure) without touching
      // the last-known isCFO/hasCopilotAccess values.
      markRoleFetchAttempted: () => {
        set({ roleFetched: true });
      },

      // Clear all permissions (call on logout or session end)
      clearPermissions: () => {
        set({
          auth0Permissions: [],
          userPermissions: [],
          allPermissions: [],
          isCFO: false,
          hasCopilotAccess: false,
          roleFetched: false,
          lastFetched: null,
        });
      },

      // Check if user has a specific permission
      hasPermission: (permission: string) => {
        if (AUTH_BYPASS_ENABLED) return true;
        const state = get();
        return state.allPermissions.includes(permission);
      },

      // Check if user has specific permission for an activity
      hasActivityPermission: (activityId: string, permissionType: PermissionType) => {
        if (AUTH_BYPASS_ENABLED) return true;
        const state = get();
        return state.userPermissions.some((p) => p.activityId === activityId && p.type === permissionType);
      },

      // Check if user is admin (has admin permission)
      isAdmin: () => {
        if (AUTH_BYPASS_ENABLED) return true;
        const state = get();
        // Check both Auth0 permissions and API permissions
        return (
          state.allPermissions.includes('admin') ||
          state.allPermissions.includes('ADMIN') ||
          state.userPermissions.some((p) => p.type === PermissionType.Admin)
        );
      },

      // Check if user is owner of specific activity
      isOwner: (activityId: string) => {
        if (AUTH_BYPASS_ENABLED) return true;
        const state = get();
        return state.userPermissions.some((p) => p.activityId === activityId && p.type === PermissionType.Owner);
      },

      // Check if cache is still valid
      isCacheValid: () => {
        const state = get();
        return state.lastFetched !== null && Date.now() - state.lastFetched < state.cacheDuration;
      },
    }),
    {
      name: 'permissions-store',
    },
  ),
);
